import { not } from '@ember/object/computed';
import { computed } from '@ember/object';
import Model from '@ember-data/model';
import attr from 'ember-data/attr';
import backfillTypes from 'cio/utils/backfill-types';

export type BackfillState = 'Queued' | 'InProgress' | 'Failure' | 'Success' | 'Retrying' | 'Canceled';
export type BackfillPriority = 'Priority' | 'Default' | 'Background';

const PRIORITY_DESCRIPTIONS = {
  Priority: 'High',
  Default: 'Medium',
  Background: 'Low',
};

export default class BackfillModel extends Model {
  @attr('string') objectId!: string;
  @attr('string') objectVersion!: string;
  @attr('string') priority!: BackfillPriority;
  @attr('string') type!: string;
  @attr('string') state!: BackfillState;
  @attr('string') status!: string;
  @attr('number') lastUpdated!: number;
  @attr('number') completed!: number;
  @attr('number') created!: number;
  @attr('number') started!: number;

  @attr('string') private error!: string; // private: use public "displayedError" computed property

  @computed('state')
  get pending(): boolean {
    return !['Success', 'Failure', 'Canceled'].includes(this.state);
  }

  get priorityDescription() {
    let { priority } = this;
    return PRIORITY_DESCRIPTIONS[priority] || priority;
  }

  // original "error" property may not be cleared on the back-end and still returned in the back-end response, even for states that should never have it
  // this computed property references error against task state, returns it only when should be displayed
  @computed('state')
  get displayedError(): string {
    if (['Canceled', 'Failure'].includes(this.state)) {
      return this.error;
    }

    return '';
  }

  // Background priority tasks have no significance to the user
  @computed('{priority,type}')
  get invisible() {
    return this.priority === 'Background' || this.type === 'Alias';
  }

  @not('invisible') visible!: boolean;

  // Various type-dependent information about the backfill
  @computed('type')
  get info() {
    return backfillTypes(this.type);
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    backfill: BackfillModel;
  }
}
